// Unpublished Work © 2022-2026 Deere & Company.

import React from 'react';
import PropTypes from 'Utils/prop-type-utils';
import {fetchEffectData, useDeepMemo} from 'Utils/react-utils';
import {getEquipmentRollupsForFleet, getEquipmentRollupsForFleetRange} from 'Services/fleet-service';
import NoDataMessage from 'Ui/components/common/message/no-data-message';
import UptimeReportChart from 'Ui/features/onequip/reports/uptime-report/uptime-report-chart';
import UptimeReportDataTable, {getHeaderColumnName} from 'Ui/features/onequip/reports/uptime-report/uptime-report-data-table';
import {ONE_HUNDRED_PERCENT} from 'Ui/constants/common-constants';
import {sortBy} from 'lodash';
import {useNavBarActions} from 'Ui/react-hooks/use-navbar-actions';
import {IconWdt} from '@deere/icons';
import {exportEquipmentUptimeReport} from 'Services/excel-service';
import {VIEW_EQUIPMENT_DETAILS} from 'Common/constants/business-activities';
import LaborReportExportDialog from 'OnLabor/report/labor-report-export-dialog';
import {getEquipmentUptimeExportContent} from 'Utils/labor-report-export-utils';
import useReportExportDialog from 'Ui/react-hooks/use-report-export-dialog';
import moment from 'moment';

function getEquipmentUptimeRatio(equipmentRollup) {
    const {
        downtime,
        uptime
    } = equipmentRollup;

    return {
        ...equipmentRollup,
        ratio: uptime / (uptime + downtime) * ONE_HUNDRED_PERCENT
    };
}

function getAggregatedRollups(rollupsForSelector) {
    const rollupsByName = rollupsForSelector.reduce((rollupMap, rollup) => {
        const {name} = rollup;

        if (!rollupMap.has(name)) {
            rollupMap.set(name, []);
        }

        rollupMap.get(name).push(rollup);

        return rollupMap;
    }, new Map());

    const aggregatedRollups = [];

    rollupsByName.forEach((rollupsForName) => {
        const aggregatedRollup = rollupsForName.reduce((aggregated, rollup) => {
            aggregated.downtime += rollup.downtime;
            aggregated.uptime += rollup.uptime;

            return aggregated;
        }, {
            ...rollupsForName[0],
            downtime: 0,
            uptime: 0
        });

        aggregatedRollups.push(getEquipmentUptimeRatio(aggregatedRollup));
    });

    return sortBy(aggregatedRollups, 'name');
}

function EquipmentUptimeReport(props) {
    const {
        dateSelect,
        endDate,
        isExportPreviewEnabled,
        membership,
        secondarySelector,
        setLoading,
        startDate,
        tertiarySelector,
        translations,
        loading,
        addToast
    } = props;

    const [equipmentRollups, setEquipmentRollups] = React.useState({});

    React.useEffect(() => fetchEffectData(async (isMounted) => {
        const equipmentRollupsData = startDate && endDate ?
            await getEquipmentRollupsForFleetRange(membership.fleetId, startDate, endDate) :
            await getEquipmentRollupsForFleet(membership.fleetId, dateSelect);

        if (isMounted()) {
            setEquipmentRollups(equipmentRollupsData);
            setLoading((prevLoading) => ({
                ...prevLoading,
                primarySelector: false,
                dateSelect: false
            }));
        }
    }), [dateSelect, endDate, membership.fleetId, startDate]);

    const equipmentRollupsWithRatio = useDeepMemo(() => {
        const {
            [secondarySelector]: equipmentRollupsForSecondary = [],
            totals
        } = equipmentRollups;

        const rollupsForSelector = equipmentRollupsForSecondary.map(getEquipmentUptimeRatio);
        const aggregatedRollupsForSelector = getAggregatedRollups(rollupsForSelector);

        if (totals) {
            return {
                rollupsForSelector: aggregatedRollupsForSelector,
                totals: getEquipmentUptimeRatio(totals)
            };
        }

        return {
            rollupsForSelector: aggregatedRollupsForSelector
        };
    }, [equipmentRollups, secondarySelector]);

    const exportContent = React.useMemo(() => getEquipmentUptimeExportContent(
        equipmentRollupsWithRatio,
        getHeaderColumnName(secondarySelector, translations),
        translations
    ), [equipmentRollupsWithRatio, secondarySelector, translations]);
    const dateRange = startDate && endDate ?
        `${moment(startDate, 'YYYYMMDD').format('MMM D, YYYY')} - ${moment(endDate, 'YYYYMMDD').format('MMM D, YYYY')}` :
        moment(dateSelect).format('MMM D, YYYY');
    const downloadReport = React.useCallback(() => exportEquipmentUptimeReport(
        equipmentRollupsWithRatio,
        secondarySelector,
        translations,
        addToast
    ), [equipmentRollupsWithRatio, secondarySelector, translations, addToast]);
    const {
        handleClose,
        handleDownload,
        handleOpen,
        isOpen
    } = useReportExportDialog(downloadReport, isExportPreviewEnabled);

    useNavBarActions([{
        disabled: loading.equipment,
        Icon: <IconWdt/>,
        onClick: handleOpen,
        myJdPermissions: VIEW_EQUIPMENT_DETAILS,
        title: 'EXPORT',
        variant: 'primary'
    }]);

    return (
        <>
            <NoDataMessage
                hasData={Object.keys(equipmentRollupsWithRatio.rollupsForSelector).length > 0}
                noDataMessage={translations.ONLINK_NO_DATA_GRAPH}
            >
                <div>
                    <div className='chart-container'>
                        <UptimeReportChart
                            tertiarySelector={tertiarySelector}
                            translations={translations}
                            uptimeReportData={equipmentRollupsWithRatio}
                        />
                    </div>
                    <UptimeReportDataTable
                        secondarySelector={secondarySelector}
                        translations={translations}
                        uptimeReportData={equipmentRollupsWithRatio}
                    />
                </div>
            </NoDataMessage>
            {
                isOpen &&
                <LaborReportExportDialog
                    dateRange={dateRange}
                    onCancel={handleClose}
                    onDownload={handleDownload}
                    rows={[
                        exportContent.header,
                        ...exportContent.rows,
                        ...exportContent.footer ? [exportContent.footer] : []
                    ]}
                    translations={translations}
                />
            }
        </>
    );
}

EquipmentUptimeReport.propTypes = {
    addToast: PropTypes.func,
    dateSelect: PropTypes.string,
    endDate: PropTypes.string,
    isExportPreviewEnabled: PropTypes.bool,
    loading: PropTypes.object,
    membership: PropTypes.membership,
    secondarySelector: PropTypes.string,
    setLoading: PropTypes.func,
    startDate: PropTypes.string,
    tertiarySelector: PropTypes.string,
    translations: PropTypes.translations
};

export default EquipmentUptimeReport;
